import { apiRequest } from "../util/apiUtils";

const REORDER_VS_ORDER_TIME_BUFFER_MS = 2000;

/** Best-effort “when was this line last touched” — client `createdAt`, or server cart `createdAt` / `updatedAt`. */
const cartLineActivityTimeMs = (item) => {
  const candidates = [item?.createdAt, item?.updatedAt]
    .filter(Boolean)
    .map((t) => new Date(t).getTime())
    .filter((n) => !Number.isNaN(n));
  if (!candidates.length) return NaN;
  return Math.max(...candidates);
};

/**
 * True when this cart line was added or updated after the book's last completed order (re-order).
 * Without any timestamp, an ordered book's line cannot be told apart from stale storage and is dropped.
 */
const cartLineLooksLikePostOrderReorder = (item, book) => {
  if (!book?.lastOrderCreatedAt) return false;
  const lineMs = cartLineActivityTimeMs(item);
  const orderMs = new Date(book.lastOrderCreatedAt).getTime();
  if (Number.isNaN(lineMs) || Number.isNaN(orderMs)) return false;
  return lineMs > orderMs + REORDER_VS_ORDER_TIME_BUFFER_MS;
};

/**
 * Single source of truth for “keep this line for this book” (used by prune + Review “already in cart”).
 */
export const cartLineShouldRemainForBook = (item, book) => {
  if (!book || !item?.bookId) return false;
  if (String(item.bookId) !== String(book._id)) return false;
  if (!book.isOrderPlaces) return true;
  return cartLineLooksLikePostOrderReorder(item, book);
};

/**
 * Drops lines for unknown book IDs and stale lines for books already ordered before the line was added.
 * Re-orders still work when the line has a timestamp after the last order: client `createdAt`, or
 * server cart rows use Mongoose `createdAt` / `updatedAt` via `cartLineActivityTimeMs`.
 * Persists the result to `selectedPlan` and emits `cartUpdated` when storage changes.
 *
 * `selectedPlan` is shared by every tab, so deleting from it on a book list that isn't authoritative
 * empties the cart everywhere. The caller must vouch for `myBooks` by passing `booksLoadedAt` (from
 * `selectBooksLoadedAt`): the time this document's book list for the signed-in customer started a
 * load that succeeded. Without it (signed out, still loading, load failed, a different customer)
 * prune is a no-op. A line for a book missing from `myBooks` is only dropped when the line predates
 * that load; a newer one may be a book another tab has created since.
 * @param {Array<{ bookId?: string }>} myBooks
 * @param {number|null} booksLoadedAt
 * @returns {Array} remaining cart items (the stored cart, untouched, when prune is a no-op)
 */
export const pruneSelectedPlanLocalStorage = (myBooks = [], booksLoadedAt = null) => {
  try {
    const raw = localStorage.getItem("selectedPlan");
    if (!raw) return [];

    let parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      parsed = [parsed];
    }
    if (!Number.isFinite(booksLoadedAt)) {
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      localStorage.removeItem("selectedPlan");
      resetCartSyncState();
      window.dispatchEvent(new Event("cartUpdated"));
      return [];
    }

    const valid = parsed.filter((item) => {
      const book = myBooks.find((b) => b._id === item?.bookId);
      if (!book) {
        // Possibly a book created elsewhere after our list was fetched. Lines without a timestamp
        // can't be told apart, and are dropped as before.
        return item?.bookId != null && cartLineActivityTimeMs(item) >= booksLoadedAt;
      }
      return cartLineShouldRemainForBook(item, book);
    });

    const nextSerialized = valid.length ? JSON.stringify(valid) : null;
    const prev = raw;

    if (nextSerialized === null) {
      if (prev) {
        localStorage.removeItem("selectedPlan");
        resetCartSyncState();
        window.dispatchEvent(new Event("cartUpdated"));
      }
      return [];
    }

    if (prev !== nextSerialized) {
      localStorage.setItem("selectedPlan", nextSerialized);
      resetCartSyncState();
      window.dispatchEvent(new Event("cartUpdated"));
    }

    return valid;
  } catch (e) {
    console.error("pruneSelectedPlanLocalStorage:", e);
    return [];
  }
};

/** POST /shopify-cart/add returns `{ status, message, data: lineItem }` on the axios `response.data`. */
export const getCartLineMongoIdFromAddResponse = (axiosResponse) => {
  const body = axiosResponse?.data;
  if (!body || typeof body !== "object") return undefined;
  return body.data?._id ?? body._id;
};

export const getShopifyCart = async () => {
  try {
    const res = await apiRequest("get", "/shopify-cart");
    return res?.data;
  } catch (error) {
    return error;
  }
};

const readLocalCartLines = () => {
  try {
    const raw = localStorage.getItem("selectedPlan");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return (Array.isArray(parsed) ? parsed : [parsed]).filter(Boolean);
  } catch {
    return [];
  }
};

const bookIdsOf = (lines) =>
  new Set(lines.map((line) => line?.bookId).filter(Boolean).map(String));

/** Book ids in this browser's cart (`selectedPlan`). */
export const readLocalCartBookIds = () => bookIdsOf(readLocalCartLines());

/**
 * Book ids in the customer's cart, for deciding where a returning customer lands. The browser's
 * cart is the source of truth when it has lines (same rule as UserRoute's cart sync); a browser
 * with none (new device, cleared storage) is hydrated from the server copy here, so the
 * Finalize Order screen it may be sent to isn't empty. Any failure reads as an empty cart.
 */
export const loadCartBookIds = async () => {
  const local = readLocalCartLines();
  if (local.length) return bookIdsOf(local);
  const res = await getShopifyCart();
  if (!Array.isArray(res?.data) || !res.data.length) return new Set();
  localStorage.setItem("selectedPlan", JSON.stringify(res.data));
  window.dispatchEvent(new Event("cartUpdated"));
  return bookIdsOf(res.data);
};

export const addToShopifyCart = async (params) => {
  try {
    const res = await apiRequest("post", "/shopify-cart/add", {
      body: { ...params },
    });
    return res;
  } catch (error) {
    return error;
  }
};

export const updateShopifyCartQuantity = async (params) => {
  try {
    const res = await apiRequest("patch", "/shopify-cart/update", {
      body: { ...params },
    });
    return res;
  } catch (error) {
    return error;
  }
};

export const removeShopifyCartItem = async (id) => {
  try {
    const res = await apiRequest("delete", `/shopify-cart/remove/${id}`);
    return res;
  } catch (error) {
    return error;
  }
};

export const clearShopifyCart = async () => {
  try {
    // The server cart is now empty, so the next push must go out even if the browser rebuilds
    // the identical cart (a re-order of the same book right after checkout).
    resetCartSyncState();
    const res = await apiRequest("delete", "/shopify-cart/clear");
    return res;
  } catch (error) {
    return error;
  }
};

/** Stable identity of a cart line. The browser can only hold one line per book + variant. */
export const cartLineKey = (item) =>
  `${item?.bookId ?? ""}::${item?.shopifyVariantId || item?.variantId || item?.id || ""}`;

/** Everything the server needs to know about the cart, so an unchanged cart can skip the round trip. */
const cartSignature = (items) =>
  JSON.stringify(
    items.map((i) => [cartLineKey(i), Number(i?.quantity) || 1]).sort((a, b) => (a[0] < b[0] ? -1 : 1))
  );

const readLocalCart = () => {
  try {
    const raw = localStorage.getItem("selectedPlan");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const arr = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
    return arr.filter(Boolean);
  } catch (e) {
    console.error("Failed to parse local cart for sync:", e);
    return [];
  }
};

const toServerLine = (item) => ({
  bookId: item.bookId,
  shopifyVariantId: item.shopifyVariantId || item.variantId || item.id,
  shopifyProductId: item.shopifyProductId || item.productId || "",
  quantity: item.quantity,
  title: item.title,
  name: item.name,
  description: item.description,
  image: item.image,
  previewUrl: item.previewUrl,
  coverImage: item.coverImage,
  planPricingData: item.planPricingData,
  recipient: item.recipient,
  version: item.version,
  features: item.features,
  offerType: item.offerType,
  highLightTag: item.highLightTag,
  highLightText: item.highLightText,
});

/** POST /shopify-cart/sync — idempotent whole-cart replace. */
const syncShopifyCart = async (items) => {
  try {
    return await apiRequest("post", "/shopify-cart/sync", {
      body: { items: items.map(toServerLine) },
    });
  } catch (error) {
    return error;
  }
};

/**
 * Write the server's line `_id`s into whatever `selectedPlan` holds RIGHT NOW, matched by
 * book + variant.
 *
 * This never writes back a snapshot: a book added to the cart while the sync was in flight
 * used to be erased by the stale array the sync had been holding since it started, which is
 * how finished books vanished from Finalize Order mid-generation.
 */
const mergeServerIdsIntoStoredCart = (serverRows = []) => {
  const idByKey = new Map();
  serverRows.forEach((row) => {
    if (row?._id) idByKey.set(cartLineKey(row), row._id);
  });
  if (!idByKey.size) return;

  const current = readLocalCart();
  if (!current.length) return;

  let changed = false;
  const merged = current.map((item) => {
    const id = idByKey.get(cartLineKey(item));
    if (!id || item._id === id) return item;
    changed = true;
    return { ...item, _id: id };
  });

  if (!changed) return;
  localStorage.setItem("selectedPlan", JSON.stringify(merged));
  // Finalize Order keeps its own copy of the cart. Without this it keeps the copy that has no
  // `_id`, writes it back on the next quantity change and skips the server update.
  window.dispatchEvent(new Event("cartUpdated"));
};

/**
 * Record the server `_id` returned by a fire-and-forget POST /shopify-cart/add, in whatever
 * `selectedPlan` holds now (the customer may already have changed the quantity).
 *
 * If the line is gone, the customer removed it while /add was still in flight. The removal could
 * not name a row that did not exist yet, so the row /add just created is an orphan: delete it.
 */
export const recordCartLineId = (line, lineId) => {
  if (!lineId) return;
  const key = cartLineKey(line);
  if (!readLocalCart().some((item) => cartLineKey(item) === key)) {
    removeShopifyCartItem(lineId);
    return;
  }
  mergeServerIdsIntoStoredCart([{ ...line, _id: lineId }]);
};

/** Legacy path for an App deployed ahead of an API that has no /shopify-cart/sync yet. */
const pushCartLegacy = async (cartArray) => {
  await clearShopifyCart();
  const rows = [];
  for (const item of cartArray) {
    const res = await addToShopifyCart(toServerLine(item));
    const lineId = getCartLineMongoIdFromAddResponse(res);
    if (lineId) rows.push({ ...item, _id: lineId });
  }
  return rows;
};

let syncInFlight = null;
let resyncRequested = false;
let lastPushedSignature = null;
let legacySyncEndpoint = false;

/**
 * Forget what was last pushed — call whenever the cart is emptied or trimmed, so the next push
 * goes out even if the browser ends up rebuilding an identical cart.
 */
export const resetCartSyncState = () => {
  lastPushedSignature = null;
};

/**
 * Tests only. In the app, an API without `/shopify-cart/sync` is detected once per page load and
 * a reload re-probes, so nothing else should reset this.
 */
export const resetCartSyncEndpointForTests = () => {
  legacySyncEndpoint = false;
};

const pushCartOnce = async () => {
  const cartArray = readLocalCart();

  // An empty local cart is not "delete the server cart" — UserRoute hydrates local from the
  // server in that case, so leave the server alone, exactly as before.
  if (cartArray.length === 0) return;

  const signature = cartSignature(cartArray);
  if (signature === lastPushedSignature) return;

  let rows;
  if (!legacySyncEndpoint) {
    // apiRequest resolves a failed request to its axios response (or undefined when there was no
    // response at all), so an API that predates /shopify-cart/sync shows up as `status: 404` here.
    const res = await syncShopifyCart(cartArray);
    if (res?.status === 404) {
      legacySyncEndpoint = true;
    } else if (res?.data?.status === 200 && Array.isArray(res.data.data)) {
      rows = res.data.data;
    } else {
      console.error("Cart sync failed; will retry on the next change:", res?.message || res);
      return;
    }
  }

  if (legacySyncEndpoint) rows = await pushCartLegacy(cartArray);

  mergeServerIdsIntoStoredCart(rows);
  lastPushedSignature = signature;
};

/**
 * Push `selectedPlan` to the server. Single-flight: overlapping callers share one request, and a
 * change made while a sync is running triggers exactly one follow-up rather than a new sync per
 * caller. Unchanged carts are a no-op, which is what stops the request storm during line-art
 * generation (UserRoute re-runs this effect on every `myBooks` update).
 */
export const syncLocalCartToDB = async () => {
  if (syncInFlight) {
    resyncRequested = true;
    return syncInFlight;
  }

  syncInFlight = (async () => {
    try {
      do {
        resyncRequested = false;
        await pushCartOnce();
      } while (resyncRequested);
    } catch (error) {
      console.error("Error synchronizing local cart to DB:", error);
    } finally {
      syncInFlight = null;
    }
  })();

  return syncInFlight;
};
